import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { StarRatingDisplay } from "../../components/StarRating";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { formatDateTime } from "../../lib/format";

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: user, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminUserDetail(id ?? ""),
    queryFn: () => adminApiClient.getUserDetail(id as string),
    enabled: Boolean(id),
  });

  const setStatus = useMutation({
    mutationFn: (status: "ACTIVE" | "SUSPENDED") => adminApiClient.setUserStatus(id as string, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUserDetail(id ?? "") });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers("") });
    },
  });

  if (isLoading) return <LoadingState label="Loading user…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!user) return null;

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      <div className="row row--between">
        <h1>{user.username ?? "Unnamed user"}</h1>
        <span className={user.status === "SUSPENDED" ? "badge badge--danger" : "badge badge--success"}>
          {user.status}
        </span>
      </div>

      <div className="card stack">
        <div className="row row--between">
          <span className="muted">User ID</span>
          <span className="mono text-sm">{user.id}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Email</span>
          <span>{user.email ?? "—"}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Role</span>
          <span>{user.role}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Joined</span>
          <span>{formatDateTime(user.createdAt)}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Listings</span>
          <span>{user.listingCount}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Buyer orders</span>
          <span>{user.buyerOrderCount}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Seller orders</span>
          <span>{user.sellerOrderCount}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Seller rating</span>
          <StarRatingDisplay rating={user.sellerRating} size="sm" />
        </div>
      </div>

      <div className="card stack">
        <h2>Wallets</h2>
        {user.wallets.length === 0 && <p className="muted">No wallets.</p>}
        {user.wallets.map((w) => (
          <div key={w.id} className="row row--between">
            <span className="mono text-sm">{w.checksumAddress}</span>
            <span className="text-sm muted">
              {w.walletType} · chain {w.chainId} · {w.isActive ? "active" : "inactive"}
            </span>
          </div>
        ))}
      </div>

      <div className="card stack">
        <h2>Actions</h2>
        <div className="row">
          {user.status === "ACTIVE" ? (
            <button
              type="button"
              className="btn btn-danger"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate("SUSPENDED")}
            >
              Suspend user
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate("ACTIVE")}
            >
              Reinstate user
            </button>
          )}
        </div>
        {setStatus.isError && (
          <p className="field-error-text" role="alert">
            {describeError(setStatus.error)}
          </p>
        )}
      </div>
    </div>
  );
}
